import { useEffect } from "react"
import { Navigate, useLocation } from "react-router-dom"
import { authApi } from "@/features/auth/api/auth.api"
import { useAuthStore } from "@/store/auth.store"
import { NoAccess } from "./NoAccess"
import { ForcedPasswordChange } from "./ForcedPasswordChange"

export function FullPageLoader({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center" role="status" aria-live="polite">
      <div className="flex flex-col items-center gap-4">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <p className="text-sm text-muted-foreground">{label}</p>
      </div>
    </div>
  )
}

/**
 * Asks the API who is signed in (the session cookie is HttpOnly, so the page
 * cannot tell by itself) and admits only accounts with the required
 * capability. This is a convenience for the browser: every API call is
 * checked again on the server.
 */
export function AuthGuard({ require, children }: { require: "demo" | "admin"; children: React.ReactNode }) {
  const status = useAuthStore((s) => s.status)
  const me = useAuthStore((s) => s.me)
  const location = useLocation()

  useEffect(() => {
    if (status !== "unknown") return
    authApi.getMe()
      .then((m) => useAuthStore.getState().setMe(m))
      .catch(() => useAuthStore.getState().clear())
  }, [status])

  if (status === "unknown") return <FullPageLoader label="Checking your session…" />
  if (status === "anonymous" || !me) {
    const from = location.pathname + location.search
    return <Navigate to={`/auth/login?from=${encodeURIComponent(from)}`} replace />
  }
  // An administrator-set password must be replaced first (the API refuses everything else)
  if (me.account.must_change_password) return <ForcedPasswordChange />
  if (require === "admin" && !me.capabilities.admin_console) return <Navigate to="/" replace />
  if (require === "demo" && !me.capabilities.demo) {
    return me.capabilities.admin_console ? <Navigate to="/admin" replace /> : <NoAccess />
  }
  return <>{children}</>
}
